import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import CustomerApplicantContractApi from "../../../../infrastructure/api/customerApplicantContract";
import useCustomerPriceConfirmationWorkspace, {
  PRICE_WORKSPACE_STATE,
} from "../../../../infrastructure/hooks/useCustomerPriceConfirmationWorkspace";
import {
  CUSTOMER_OFFER_STATUS_CODES,
  normalizeCustomerPriceOfferStatus,
} from "../../../../infrastructure/utils/pricingQuoteStatus";

const OFFER_ACCEPTED = CUSTOMER_OFFER_STATUS_CODES.AcceptedByCustomer;
const OFFER_APPROVED = CUSTOMER_OFFER_STATUS_CODES.ApprovedBySolarent;

const SIGN_WORKSPACE = {
  NotAvailable: "NotAvailable",
  ReadyToSign: "ReadyToSign",
  PendingSignature: "PendingSignature",
  Signed: "Signed",
  NotSignable: "NotSignable",
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("fa-IR");
};

const normalizeOffer = (raw) => {
  if (!raw) return null;
  return {
    id: raw?.id ?? raw?.Id,
    requestId: raw?.requestId ?? raw?.RequestId,
    status: normalizeCustomerPriceOfferStatus(raw?.status ?? raw?.Status),
    customerPriceFinal: raw?.customerPriceFinal ?? raw?.CustomerPriceFinal ?? 0,
    customerAcceptedAt: raw?.customerAcceptedAt ?? raw?.CustomerAcceptedAt ?? null,
    canDownloadContract: raw?.canDownloadContract ?? raw?.CanDownloadContract,
    applicantContractId: raw?.applicantContractId ?? raw?.ApplicantContractId ?? null,
  };
};

const normalizeSignSummary = (raw) => {
  if (!raw) return null;
  return {
    contractId: raw?.contractId ?? raw?.ContractId ?? null,
    workspaceState: raw?.workspaceState ?? raw?.WorkspaceState ?? SIGN_WORKSPACE.NotAvailable,
    canDownloadPdf: raw?.canDownloadPdf ?? raw?.CanDownloadPdf ?? false,
    isSigned: raw?.isSigned ?? raw?.IsSigned ?? false,
    statusTitle: raw?.statusTitle ?? raw?.StatusTitle ?? "",
    message: raw?.message ?? raw?.Message ?? "",
  };
};

const CustomerPricingActionPanel = ({ requestId, onWorkspaceStateChange }) => {
  const navigate = useNavigate();
  const {
    loading,
    error,
    workspace,
    shouldShowPanel,
    requestPrimaryKey,
  } = useCustomerPriceConfirmationWorkspace(requestId);

  const [signSummary, setSignSummary] = useState(null);

  useEffect(() => {
    onWorkspaceStateChange?.(workspace?.workspaceState ?? null);
  }, [onWorkspaceStateChange, workspace?.workspaceState]);

  const offer = useMemo(() => normalizeOffer(workspace?.offer), [workspace?.offer]);
  const workspaceState = workspace?.workspaceState ?? "";

  const loadSignSummary = useCallback(async () => {
    if (!requestPrimaryKey) {
      setSignSummary(null);
      return;
    }
    if (offer?.status !== OFFER_ACCEPTED && offer?.status !== OFFER_APPROVED) {
      setSignSummary(null);
      return;
    }
    try {
      const res = await CustomerApplicantContractApi.getSummary(requestPrimaryKey);
      setSignSummary(res.ok ? normalizeSignSummary(res.data) : null);
    } catch {
      setSignSummary(null);
    }
  }, [offer?.status, requestPrimaryKey]);

  useEffect(() => {
    loadSignSummary();
  }, [loadSignSummary]);

  const priceOfferPath = requestPrimaryKey
    ? `/SLN/Customer/PriceOfferContract/${requestPrimaryKey}`
    : null;
  const digitalSignPath = requestPrimaryKey
    ? `/SLN/Customer/ApplicantContractSign/${requestPrimaryKey}`
    : null;

  if (!requestPrimaryKey) {
    return null;
  }

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ py: 3, textAlign: "center" }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  if (error || !shouldShowPanel) {
    return null;
  }

  const digitalReady =
    Boolean(signSummary?.contractId) ||
    Boolean(offer?.applicantContractId) ||
    Boolean(offer?.canDownloadContract) ||
    signSummary?.workspaceState === SIGN_WORKSPACE.ReadyToSign ||
    signSummary?.workspaceState === SIGN_WORKSPACE.PendingSignature;
  const digitalSigned =
    Boolean(signSummary?.isSigned) ||
    signSummary?.workspaceState === SIGN_WORKSPACE.Signed;

  const renderDigitalSignActions = () => (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: "action.hover",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={1.25}>
        {digitalSigned ? (
          <Alert severity="success" sx={{ py: 0 }}>
            قرارداد به‌صورت دیجیتال امضا شده است.
          </Alert>
        ) : digitalReady ? (
          <Alert severity="info" sx={{ py: 0 }}>
            قرارداد آماده است. برای مشاهده PDF و امضا با کد پیامکی وارد شوید.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ py: 0 }}>
            قرارداد هنوز توسط سولارنت تولید نشده است. پس از تولید از همینجا امضا
            می‌کنید.
          </Alert>
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="contained"
            color={digitalSigned ? "success" : "primary"}
            startIcon={<EditIcon />}
            onClick={() => navigate(digitalSignPath)}
          >
            {digitalSigned ? "مشاهده قرارداد امضا‌شده" : "مشاهده و امضای دیجیتال"}
          </Button>
          {priceOfferPath ? (
            <Button variant="outlined" onClick={() => navigate(priceOfferPath)}>
              جزئیات قیمت و قرارداد
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );

  const renderPriceConfirmationCard = () => {
    if (workspaceState === PRICE_WORKSPACE_STATE.NOT_SUBMITTED) {
      return (
        <Alert severity="info" data-testid="customer-price-not-submitted">
          {workspace.message || "قیمت نهایی هنوز برای شما ارسال نشده است."}
        </Alert>
      );
    }

    if (workspaceState === PRICE_WORKSPACE_STATE.REVIEW) {
      return (
        <Alert severity="info" data-testid="customer-price-review-requested">
          {workspace.message ||
            "درخواست بررسی مجدد شما برای کارشناس ارسال شده است."}
        </Alert>
      );
    }

    if (
      workspaceState === PRICE_WORKSPACE_STATE.ACCEPTED ||
      workspaceState === PRICE_WORKSPACE_STATE.AWAITING
    ) {
      return (
        <Stack spacing={1.5} data-testid="customer-price-accepted-summary">
          <Alert severity="success">
            {workspace.message ||
              "قیمت نهایی تایید شده و قرارداد آماده مشاهده و امضا است."}
          </Alert>
          {offer?.customerPriceFinal ? (
            <Typography variant="body1" fontWeight={700}>
              مبلغ نهایی: {Number(offer.customerPriceFinal).toLocaleString("fa-IR")} ریال
            </Typography>
          ) : null}
          {offer?.customerAcceptedAt ? (
            <Typography variant="body2" color="text.secondary">
              تاریخ آماده‌سازی: {formatDateTime(offer.customerAcceptedAt)}
            </Typography>
          ) : null}
          {renderDigitalSignActions()}
        </Stack>
      );
    }

    return null;
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor:
          workspaceState === PRICE_WORKSPACE_STATE.ACCEPTED ||
          workspaceState === PRICE_WORKSPACE_STATE.AWAITING
            ? "success.main"
            : undefined,
      }}
      data-testid="customer-pricing-action-panel"
    >
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AssignmentTurnedInIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>
              قیمت و قرارداد متقاضی
            </Typography>
          </Stack>
          {renderPriceConfirmationCard()}
        </Stack>
      </CardContent>
    </Card>
  );
};

CustomerPricingActionPanel.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onWorkspaceStateChange: PropTypes.func,
};

export default CustomerPricingActionPanel;
